import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SocketService } from './socket.service';
import { Conversation, ChatMessage, ChatContext, ChatPerson, ChatTeam, ChatBooking, MessageReaction, ChatAttachment } from '../models/chat.model';

/** Chat threads + live message delivery over socket.io (chat:<id> rooms). */
@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly url = `${environment.apiUrl}/chat`;
  private wired = false;
  /** Conversation rooms this client wants to be in. A reconnect starts a fresh
   *  server session with no rooms, so they are re-joined on every connect. */
  private readonly joined = new Set<string>();
  /** Emits every incoming live message (any joined conversation). */
  readonly incoming$ = new Subject<{ conversationId: string; message: ChatMessage }>();
  /** Emits live reaction updates for a message in a joined conversation. */
  readonly reactions$ = new Subject<{ conversationId: string; messageId: string; reactions: MessageReaction[] }>();
  /** Emits an edited/soft-deleted message so open threads update in place. */
  readonly messageUpdate$ = new Subject<{ conversationId: string; message: ChatMessage }>();
  /** Emits the fresh pinned-message set for a conversation. */
  readonly pins$ = new Subject<{ conversationId: string; pinned: ChatMessage[] }>();
  /** Emits typing indicators for a joined conversation. */
  readonly typing$ = new Subject<{ conversationId: string; userId: string; userName: string; typing: boolean }>();
  /** Emits a read-receipt: a participant caught up to `lastReadAt` (for "Seen"). */
  readonly read$ = new Subject<{ conversationId: string; userId: string; userName: string; lastReadAt: string }>();
  /** Emits presence changes (a user came online / went offline, with last-seen). */
  readonly presence$ = new Subject<{ userId: string; online: boolean; lastSeenAt?: string | null }>();
  /** Emits when a message is delivered to a recipient (sent→delivered→read tick). */
  readonly delivered$ = new Subject<{ conversationId: string; messageId: string; deliveredAt: string }>();
  /** Emits live-location position updates for a shared-location message. */
  readonly location$ = new Subject<{ conversationId: string; messageId: string; location: any }>();

  constructor(private http: HttpClient, private socket: SocketService) {}

  private ensureSocket(): void {
    if (!this.wired) {
      this.wired = true;
      // Re-join every open conversation whenever the session is (re)established.
      this.socket.onConnect(() => this.joined.forEach(id => this.socket.emit('join:chat', id)));
      this.socket.on('chat:message', (p: { conversationId: string; message: ChatMessage }) =>
        this.incoming$.next(p));
      this.socket.on('chat:reaction', (p: { conversationId: string; messageId: string; reactions: MessageReaction[] }) =>
        this.reactions$.next(p));
      this.socket.on('chat:message:update', (p: { conversationId: string; message: ChatMessage }) =>
        this.messageUpdate$.next(p));
      this.socket.on('chat:pins', (p: { conversationId: string; pinned: ChatMessage[] }) =>
        this.pins$.next(p));
      this.socket.on('chat:typing', (p: { conversationId: string; userId: string; userName: string; typing: boolean }) =>
        this.typing$.next(p));
      this.socket.on('chat:read', (p: { conversationId: string; userId: string; userName: string; lastReadAt: string }) =>
        this.read$.next(p));
      this.socket.on('presence:update', (p: { userId: string; online: boolean; lastSeenAt?: string | null }) =>
        this.presence$.next(p));
      this.socket.on('chat:delivered', (p: { conversationId: string; messageId: string; deliveredAt: string }) =>
        this.delivered$.next(p));
      this.socket.on('chat:location', (p: { conversationId: string; messageId: string; location: any }) =>
        this.location$.next(p));
    }
  }

  joinConversation(id: string): void {
    this.ensureSocket();
    this.joined.add(id);
    this.socket.emit('join:chat', id);
  }
  leaveConversation(id: string): void {
    this.joined.delete(id);
    this.socket.emitIfConnected('leave:chat', id);
  }
  /** Broadcast a typing state to the conversation room. The server stamps the
   *  sender's identity from the socket, so only the flag travels. */
  sendTyping(conversationId: string, _userId: string, _userName: string, typing: boolean): void {
    this.socket.emitIfConnected('chat:typing', { conversationId, typing });
  }

  listConversations(contextType?: ChatContext, contextId?: string): Observable<Conversation[]> {
    const q: string[] = [];
    if (contextType) q.push(`contextType=${encodeURIComponent(contextType)}`);
    if (contextId) q.push(`contextId=${encodeURIComponent(contextId)}`);
    return this.http.get<Conversation[]>(`${this.url}/conversations${q.length ? '?' + q.join('&') : ''}`);
  }
  getConversation(id: string): Observable<Conversation> {
    return this.http.get<Conversation>(`${this.url}/conversations/${id}`);
  }
  createConversation(contextType: ChatContext, contextId?: string, title?: string): Observable<Conversation> {
    return this.http.post<Conversation>(`${this.url}/conversations`, { contextType, contextId, title });
  }
  postMessage(conversationId: string, body: string, attachmentIds?: string[], replyToId?: string | null): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(`${this.url}/conversations/${conversationId}/messages`, { body, attachmentIds, replyToId });
  }
  /** Older-page loader for infinite scroll (messages before `beforeId`). */
  loadOlder(conversationId: string, beforeId: string, limit = 30): Observable<{ messages: ChatMessage[]; hasMore: boolean }> {
    return this.http.get<{ messages: ChatMessage[]; hasMore: boolean }>(
      `${this.url}/conversations/${conversationId}/messages?before=${beforeId}&limit=${limit}`);
  }
  /** Edit own message body. */
  editMessage(messageId: string, body: string): Observable<ChatMessage> {
    return this.http.patch<ChatMessage>(`${this.url}/messages/${messageId}`, { body });
  }
  /** Soft-delete a message (sender or admin). */
  deleteMessage(messageId: string): Observable<ChatMessage> {
    return this.http.delete<ChatMessage>(`${this.url}/messages/${messageId}`);
  }
  /** Pin / unpin a message to the thread top. */
  pinMessage(messageId: string, pinned: boolean): Observable<{ ok: boolean; pinned: boolean; pinnedMessages: ChatMessage[] }> {
    return this.http.post<{ ok: boolean; pinned: boolean; pinnedMessages: ChatMessage[] }>(`${this.url}/messages/${messageId}/pin`, { pinned });
  }
  /** Search within a conversation's history. */
  searchMessages(conversationId: string, q: string): Observable<{ messages: ChatMessage[]; total: number }> {
    return this.http.get<{ messages: ChatMessage[]; total: number }>(
      `${this.url}/conversations/${conversationId}/search?q=${encodeURIComponent(q)}`);
  }
  /** Presence seed: ids currently online + everyone's last-seen time (ISO). */
  getPresence(): Observable<{ online: string[]; lastSeen: Record<string, string> }> {
    return this.http.get<{ online: string[]; lastSeen: Record<string, string> }>(`${this.url}/presence`);
  }

  /* ── WP 1211: team channels + direct (1-to-1) chats ── */
  /** Active users (excluding self) to start a direct chat with. */
  listPeople(): Observable<ChatPerson[]> {
    return this.http.get<ChatPerson[]>(`${this.url}/people`);
  }
  /** The standing team channels the caller belongs to (by role). */
  listTeams(): Observable<ChatTeam[]> {
    return this.http.get<ChatTeam[]>(`${this.url}/teams`);
  }
  /** Bookings for the booking-chat picker (staff-accessible, no bookings module perm). */
  listBookings(q?: string): Observable<ChatBooking[]> {
    const qs = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    return this.http.get<ChatBooking[]>(`${this.url}/bookings${qs}`);
  }
  /** Open (or reuse) a team channel: 'driver' | 'backoffice'. */
  openTeam(key: string): Observable<Conversation> {
    return this.http.post<Conversation>(`${this.url}/conversations`, { contextType: 'team', contextId: key });
  }
  /** Open (or reuse) a one-to-one chat with another user. */
  openDirect(userId: string): Observable<Conversation> {
    return this.http.post<Conversation>(`${this.url}/conversations/direct`, { userId });
  }
  /** Clear the caller's unread count for a conversation. */
  markRead(conversationId: string): Observable<{ ok: boolean; lastReadAt: string }> {
    return this.http.post<{ ok: boolean; lastReadAt: string }>(`${this.url}/conversations/${conversationId}/read`, {});
  }
  /** Mute/unmute push + chime for a conversation (still counts unread). */
  setMuted(conversationId: string, muted: boolean): Observable<{ ok: boolean; muted: boolean }> {
    return this.http.post<{ ok: boolean; muted: boolean }>(`${this.url}/conversations/${conversationId}/mute`, { muted });
  }

  /* ── WP 1211 phase 2: groups, reactions, attachments ── */
  /** Create a named group with the picked members (creator added automatically). */
  createGroup(title: string, memberIds: string[]): Observable<Conversation> {
    return this.http.post<Conversation>(`${this.url}/conversations/group`, { title, memberIds });
  }
  /** Add members to an existing group. */
  addMembers(conversationId: string, memberIds: string[]): Observable<Conversation> {
    return this.http.post<Conversation>(`${this.url}/conversations/${conversationId}/members`, { memberIds });
  }
  /** Leave a group. */
  leave(conversationId: string): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.url}/conversations/${conversationId}/leave`, {});
  }
  /** Rename a group. */
  renameGroup(conversationId: string, title: string): Observable<Conversation> {
    return this.http.patch<Conversation>(`${this.url}/conversations/${conversationId}`, { title });
  }
  /** Remove a member from a group. */
  removeMember(conversationId: string, userId: string): Observable<Conversation> {
    return this.http.delete<Conversation>(`${this.url}/conversations/${conversationId}/members/${userId}`);
  }
  /** Delete a chat. A group is destroyed for everyone; a direct chat is removed
   *  for the caller only and comes back if they message the person again.
   *  `scope` says which happened. */
  deleteConversation(conversationId: string): Observable<{ ok: boolean; scope: 'everyone' | 'me' }> {
    return this.http.delete<{ ok: boolean; scope: 'everyone' | 'me' }>(`${this.url}/conversations/${conversationId}`);
  }
  /** @deprecated use deleteConversation — kept for the group manage dialog. */
  deleteGroup(conversationId: string): Observable<{ ok: boolean }> {
    return this.deleteConversation(conversationId);
  }
  /** Upload a file (images + PDF, 10 MB); returns an unlinked attachment.
   *  `durationSec` is sent for voice messages so the player shows a length. */
  uploadAttachment(file: File, durationSec?: number): Observable<ChatAttachment> {
    const fd = new FormData();
    fd.append('file', file);
    if (durationSec != null) fd.append('durationSec', String(Math.round(durationSec)));
    return this.http.post<ChatAttachment>(`${this.url}/attachments`, fd);
  }
  /** Forward a message into another conversation (with "Forwarded from" attribution). */
  forwardMessage(messageId: string, conversationId: string): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(`${this.url}/messages/${messageId}/forward`, { conversationId });
  }

  /* ── Tier 2 + Tier 3 ── */
  /** Global search across every chat the caller can see. */
  searchAll(q: string): Observable<{ messages: (ChatMessage & { conversationTitle?: string })[]; total: number }> {
    return this.http.get<{ messages: (ChatMessage & { conversationTitle?: string })[]; total: number }>(
      `${this.url}/search?q=${encodeURIComponent(q)}`);
  }
  /** Rich per-chat notification prefs: boolean mute, snooze-until, custom sound. */
  setNotifyPrefs(conversationId: string, prefs: { muted?: boolean; muteUntil?: string | null; notifySound?: string | null }):
    Observable<{ ok: boolean; muted: boolean; muteUntil: string | null; notifySound: string | null }> {
    return this.http.post<{ ok: boolean; muted: boolean; muteUntil: string | null; notifySound: string | null }>(
      `${this.url}/conversations/${conversationId}/notify`, prefs);
  }
  /** Pin / archive / folder a conversation for the caller (list organisation). */
  organizeConversation(conversationId: string, opts: { pinned?: boolean; archived?: boolean; folder?: string | null }):
    Observable<{ ok: boolean; pinned: boolean; archived: boolean; folder: string | null }> {
    return this.http.post<{ ok: boolean; pinned: boolean; archived: boolean; folder: string | null }>(
      `${this.url}/conversations/${conversationId}/organize`, opts);
  }
  /** Acknowledge a message was delivered to this client (drives the double-tick). */
  markDelivered(messageId: string): Observable<{ ok: boolean; deliveredAt?: string }> {
    return this.http.post<{ ok: boolean; deliveredAt?: string }>(`${this.url}/messages/${messageId}/delivered`, {});
  }
  /** Schedule a message to be sent at a future time. */
  scheduleMessage(conversationId: string, body: string, scheduledAt: string): Observable<{ ok: boolean; id: string; scheduledAt: string }> {
    return this.http.post<{ ok: boolean; id: string; scheduledAt: string }>(
      `${this.url}/conversations/${conversationId}/schedule`, { body, scheduledAt });
  }
  /** List the caller's pending scheduled messages for a conversation. */
  listScheduled(conversationId: string): Observable<{ id: string; body: string; scheduledAt: string }[]> {
    return this.http.get<{ id: string; body: string; scheduledAt: string }[]>(
      `${this.url}/conversations/${conversationId}/scheduled`);
  }
  /** Cancel a pending scheduled message. */
  cancelScheduled(messageId: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.url}/messages/${messageId}/schedule`);
  }
  /** Translate a message body into a target language (via Claude); not stored. */
  translateMessage(messageId: string, target: string): Observable<{ target: string; text: string }> {
    return this.http.post<{ target: string; text: string }>(`${this.url}/messages/${messageId}/translate`, { target });
  }
  /** Post a poll message. */
  createPoll(conversationId: string, question: string, options: string[], multi: boolean): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(`${this.url}/conversations/${conversationId}/poll`, { question, options, multi });
  }
  /** Cast / toggle a poll vote. */
  votePoll(messageId: string, optionId: string): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(`${this.url}/messages/${messageId}/poll/vote`, { optionId });
  }
  /** Post a sticker / GIF (key or image url), optionally as its own message. */
  sendSticker(conversationId: string, sticker: string): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(`${this.url}/conversations/${conversationId}/messages`, { sticker });
  }
  /** Search GIFs (proxies GIPHY server-side). Empty q → trending. */
  searchGifs(q: string): Observable<{ gifs: { id: string; preview: string; url: string }[] }> {
    return this.http.get<{ gifs: { id: string; preview: string; url: string }[] }>(
      `${this.url}/gifs${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`);
  }
  /** Share a location. durationMin > 0 → a live (moving) share; 0 → static pin. */
  shareLocation(conversationId: string, lat: number, lng: number, durationMin: number): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(`${this.url}/conversations/${conversationId}/location`, { lat, lng, durationMin });
  }
  /** Push a live-location position update (sharer's client, periodic). */
  updateLocation(messageId: string, lat: number, lng: number): Observable<{ ok: boolean; live: boolean; location: any }> {
    return this.http.patch<{ ok: boolean; live: boolean; location: any }>(`${this.url}/messages/${messageId}/location`, { lat, lng });
  }
  /** End a live-location share early. */
  stopLocation(messageId: string): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.url}/messages/${messageId}/location/stop`, {});
  }
  /** Toggle an emoji reaction on a message. */
  react(messageId: string, emoji: string): Observable<{ messageId: string; reactions: MessageReaction[] }> {
    return this.http.post<{ messageId: string; reactions: MessageReaction[] }>(`${this.url}/messages/${messageId}/react`, { emoji });
  }
}
